import axios from "axios";

/**
 * Pick the right backend origin.
 *
 * In production (turbocimi.com), the Kubernetes ingress proxies `/api` to the
 * backend service, so any host that's NOT a preview/development one should call
 * the API on its own origin. This protects us from a stale `REACT_APP_BACKEND_URL`
 * baked into the production build pointing at an old preview URL.
 */
function resolveBackendUrl() {
    const envUrl = process.env.REACT_APP_BACKEND_URL || "";
    if (typeof window === "undefined") return envUrl;
    const host = window.location.hostname;
    // Treat anything that's not an Emergent dev host as production → use same origin.
    const isDevHost = /\.preview\.emergentagent\.com$|\.emergent\.host$|^localhost$|^127\.0\.0\.1$/i.test(host);
    if (!isDevHost) return window.location.origin;
    return envUrl;
}

export const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "tc_auth_token";

export function getAuthToken() {
    try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}

export function setAuthToken(token) {
    try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    } catch {}
}

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// Attach Bearer token (fallback for iOS Safari ITP that blocks cross-site cookies)
api.interceptors.request.use((config) => {
    const t = getAuthToken();
    if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
});

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

/**
 * Download a file from an authenticated admin endpoint.
 * Works on iOS Safari (cookies blocked) because we explicitly attach the
 * Bearer token as an Authorization header. Triggers a browser download.
 *
 * @param {string} path  — API path starting with `/`, eg `/admin/products/export`
 * @param {string} [filename] — fallback filename if server doesn't set one
 * @param {object} [params] — query-string params (object → URLSearchParams)
 * @param {object} [opts] — { method: "POST", body: {...} } for POST endpoints
 */
export async function fetchPdfBlobUrl(path, { params = null, body = undefined } = {}) {
    const url = new URL(`${API}${path}`);
    Object.entries(params || {}).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") url.searchParams.set(k, v);
    });
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const init = { credentials: "include", headers };
    if (body !== undefined) {
        init.method = "POST";
        headers["Content-Type"] = "application/json";
        init.body = JSON.stringify(body);
    }
    const res = await fetch(url.toString(), init);
    if (!res.ok) throw new Error(`PDF failed (HTTP ${res.status})`);
    return URL.createObjectURL(new Blob([await res.blob()], { type: "application/pdf" }));
}

export async function printAuthedPdf(path, params = null) {
    const blobUrl = await fetchPdfBlobUrl(path, { params });
    // Print only the PDF document (not the whole page) via a hidden iframe
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    iframe.src = blobUrl;
    iframe.onload = () => {
        try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        } catch {
            window.open(blobUrl, "_blank");
        }
        setTimeout(() => { iframe.remove(); URL.revokeObjectURL(blobUrl); }, 60000);
    };
    document.body.appendChild(iframe);
}

export async function downloadAuthed(path, filename, params = null, opts = {}) {
    const url = new URL(`${API}${path}`);
    if (params) {
        Object.entries(params).forEach(([k, v]) => {
            if (v !== null && v !== undefined && v !== "") url.searchParams.set(k, v);
        });
    }
    const token = getAuthToken();
    const method = (opts.method || "GET").toUpperCase();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const init = { method, credentials: "include", headers };
    if (opts.body !== undefined) {
        headers["Content-Type"] = "application/json";
        init.body = JSON.stringify(opts.body);
    }
    const res = await fetch(url.toString(), init);
    if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
    let outName = filename || "download";
    const cd = res.headers.get("Content-Disposition") || "";
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    if (match) outName = decodeURIComponent(match[1]);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = outName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
}
